/**
 * Cookie Consent Banner Component
 * Gestion du consentement RGPD pour les cookies
 */
import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, Settings, Check } from 'lucide-react';
import { Button } from './ui/button';

const COOKIE_CONSENT_KEY = 'ygo_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'ygo_cookie_preferences';

// Durée de validité du consentement (365 jours)
const CONSENT_DURATION = 365 * 24 * 60 * 60 * 1000;

export const getCookieConsent = () => {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (consent) {
      const parsed = JSON.parse(consent);
      // Vérifier si le consentement n'a pas expiré
      if (parsed.timestamp && Date.now() - parsed.timestamp < CONSENT_DURATION) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Error reading cookie consent:', e);
  }
  return null;
};

export const getCookiePreferences = () => {
  try {
    const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    return prefs ? JSON.parse(prefs) : null;
  } catch (e) {
    return null;
  }
};

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Toujours actif
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Vérifier si le consentement a déjà été donné
    const consent = getCookieConsent();
    if (!consent) {
      // Afficher la bannière après un court délai
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Charger les préférences existantes
      const savedPrefs = getCookiePreferences();
      if (savedPrefs) {
        setPreferences(savedPrefs);
      }
    }
  }, []);

  const saveConsent = (accepted, prefs = preferences) => {
    const consentData = {
      accepted,
      timestamp: Date.now(),
      version: '1.0'
    };
    
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    
    setVisible(false);
    
    // Recharger la page pour appliquer les préférences (notamment GTM)
    if (accepted && prefs.analytics) {
      window.location.reload();
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    saveConsent(true, allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(onlyNecessary);
    saveConsent(false, onlyNecessary);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences.analytics || preferences.marketing || preferences.functional, preferences);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center p-4 pointer-events-none">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-auto"
        onClick={() => {}} // Empêche la fermeture en cliquant sur l'overlay
      />
      
      {/* Banner */}
      <div className="relative w-full max-w-4xl bg-[#1F1F1F] border border-[#C9A24D]/20 shadow-2xl pointer-events-auto animate-in slide-in-from-bottom-4 duration-500">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#C9A24D]/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#C9A24D]/10 flex items-center justify-center">
              <Cookie className="w-5 h-5 text-[#C9A24D]" />
            </div>
            <div>
              <h3 className="text-[#E5E5E5] font-semibold">Gestion des cookies</h3>
              <p className="text-[#E5E5E5]/50 text-xs">Votre vie privée, notre priorité</p>
            </div>
          </div>
          <button
            onClick={handleRejectAll}
            className="text-[#E5E5E5]/40 hover:text-[#E5E5E5] transition-colors"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {!showDetails ? (
            <>
              <p className="text-[#E5E5E5]/70 text-sm mb-4">
                Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic et personnaliser le contenu. 
                En cliquant sur "Tout accepter", vous consentez à l'utilisation de tous les cookies. 
                Vous pouvez également personnaliser vos préférences ou refuser les cookies non essentiels.
              </p>
              
              <div className="flex items-center gap-2 text-xs text-[#E5E5E5]/50 mb-4">
                <Shield className="w-4 h-4 text-[#C9A24D]" />
                <span>Conformément au RGPD et à la directive ePrivacy</span>
              </div>
            </>
          ) : (
            <div className="space-y-4 mb-4">
              {/* Cookies nécessaires */}
              <div className="p-3 bg-[#0B0B0B] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-400" />
                    <span className="text-[#E5E5E5] font-medium text-sm">Cookies nécessaires</span>
                  </div>
                  <span className="text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded">Toujours actif</span>
                </div>
                <p className="text-[#E5E5E5]/50 text-xs">
                  Ces cookies sont essentiels au fonctionnement du site (authentification, sécurité, préférences de base).
                </p>
              </div>

              {/* Cookies analytiques */}
              <div className="p-3 bg-[#0B0B0B] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#E5E5E5] font-medium text-sm">Cookies analytiques</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-[#3F3F3F] peer-checked:bg-[#C9A24D] rounded-full transition-colors">
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${preferences.analytics ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </label>
                </div>
                <p className="text-[#E5E5E5]/50 text-xs">
                  Google Analytics et Tag Manager pour analyser le trafic et améliorer nos services.
                </p>
              </div>

              {/* Cookies fonctionnels */}
              <div className="p-3 bg-[#0B0B0B] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#E5E5E5] font-medium text-sm">Cookies fonctionnels</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={(e) => setPreferences(p => ({ ...p, functional: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-[#3F3F3F] peer-checked:bg-[#C9A24D] rounded-full transition-colors">
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${preferences.functional ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </label>
                </div>
                <p className="text-[#E5E5E5]/50 text-xs">
                  Mémoriser vos préférences (langue, appareil de confiance) pour une meilleure expérience.
                </p>
              </div>

              {/* Cookies marketing */}
              <div className="p-3 bg-[#0B0B0B] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#E5E5E5] font-medium text-sm">Cookies marketing</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences(p => ({ ...p, marketing: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-[#3F3F3F] peer-checked:bg-[#C9A24D] rounded-full transition-colors">
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${preferences.marketing ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </label>
                </div>
                <p className="text-[#E5E5E5]/50 text-xs">
                  Publicités personnalisées et suivi des conversions.
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center gap-3">
            {!showDetails ? (
              <>
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="w-full sm:w-auto border-[#E5E5E5]/20 text-[#E5E5E5] hover:bg-[#E5E5E5]/10"
                >
                  Tout refuser
                </Button>
                <Button
                  onClick={() => setShowDetails(true)}
                  variant="outline"
                  className="w-full sm:w-auto border-[#C9A24D]/30 text-[#C9A24D] hover:bg-[#C9A24D]/10"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Personnaliser
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="w-full sm:w-auto bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
                >
                  Tout accepter
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => setShowDetails(false)}
                  variant="outline"
                  className="w-full sm:w-auto border-[#E5E5E5]/20 text-[#E5E5E5] hover:bg-[#E5E5E5]/10"
                >
                  Retour
                </Button>
                <Button
                  onClick={handleSavePreferences}
                  className="w-full sm:w-auto bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
                >
                  Enregistrer mes préférences
                </Button>
              </>
            )}
          </div>

          {/* Legal links */}
          <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-[#C9A24D]/10">
            <a href="/legal/privacy" className="text-xs text-[#C9A24D] hover:underline">
              Politique de confidentialité
            </a>
            <span className="text-[#E5E5E5]/20">|</span>
            <a href="/legal/cookies" className="text-xs text-[#C9A24D] hover:underline">
              Politique des cookies
            </a>
            <span className="text-[#E5E5E5]/20">|</span>
            <a href="/legal/terms" className="text-xs text-[#C9A24D] hover:underline">
              CGU
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
